'use client';

import { useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import toast from 'react-hot-toast';
import { useYandexMaps } from '../../../../hooks/useYandexMaps';
import { ReportWithDetails } from '../../../../types/shared';
import { useTrafficGlobalListener } from '../../contexts/TrafficGlobalEvents';
import { createBalloonLayout } from './createBalloonLayout';

const EVENT_TYPE_ICONS: Record<string, string> = {
  ACCIDENT: '/traffic/accident.png',
  CONGESTION: '/traffic/congestion.jpg',
  ROAD_WORKS: '/traffic/roadworks.png',
  OTHER: '/traffic/other.jpg',
};

export interface TrafficMapHandle {
  panTo: (coords: number[]) => void;
}

type Props = {
  reports: ReportWithDetails[];
  onMapClick: (coords: number[]) => void;
  isSelectMode: boolean;
  shouldLoad?: boolean;
};

type Sig = string;

function formatTime(date: string | Date) {
  return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ✅ Сигнатура — только то, что реально влияет на отображение в балуне/иконке
function makeSig(r: ReportWithDetails): Sig {
  return [
    r.likes,
    r.dislikes,
    r.comment,
    r.imageUrl ?? '',
    r.type,
    r.author.name,
    String(r.createdAt),
    r.myVote ?? '',
    r.comments.length,
  ].join('|');
}

export function useTrafficMap(
  { reports, onMapClick, isSelectMode, shouldLoad = true }: Props,
  ref: any,
) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const objectManagerRef = useRef<any>(null);

  const isSelectModeRef = useRef(isSelectMode);
  const onMapClickRef = useRef(onMapClick);

  // ✅ Вместо Map<id, report> — Map<id, sig>
  const reportsIndexRef = useRef<Map<string, Sig>>(new Map());

  const { isLoaded: isMapsLoaded } = useYandexMaps(
    process.env.NEXT_PUBLIC_YANDEX_MAPS_API_KEY!,
    shouldLoad,
  );

  const toFeature = useCallback((report: ReportWithDetails) => {
    const userVote = report.myVote ?? null;

    return {
      type: 'Feature',
      id: report.id,
      geometry: {
        type: 'Point',
        coordinates: [report.latitude, report.longitude],
      },
      properties: {
        reportId: report.id,
        comment: report.comment,
        imageUrl: report.imageUrl,
        likeCount: report.likes,
        dislikeCount: report.dislikes,
        commentCount: report.comments.length,
        userVoteType: userVote,
        authorName: report.author.name,
        time: formatTime(report.createdAt),
      },
      options: {
        iconImageHref: EVENT_TYPE_ICONS[report.type] || EVENT_TYPE_ICONS.OTHER,
      },
    };
  }, []);

  useImperativeHandle(ref, () => ({
    panTo(coords: number[]) {
      mapRef.current?.panTo(coords, { duration: 800, flying: true, zoom: 15 });
    },
  }));

  // --- Optimistic vote handler (из балуна) ---
  const handleBalloonAction = useCallback(async (reportId: string, action: string) => {
    const om = objectManagerRef.current;
    if (!om) return;

    const obj = om.objects.getById(reportId);
    if (!obj) return;

    const props = obj.properties ?? {};
    const targetType = action === 'confirm' ? 'LIKE' : 'DISLIKE';

    let likeCount = Number(props.likeCount ?? 0);
    let dislikeCount = Number(props.dislikeCount ?? 0);
    let userVoteType: 'LIKE' | 'DISLIKE' | null = props.userVoteType ?? null;

    if (userVoteType === targetType) {
      // toggle off
      if (targetType === 'LIKE') likeCount -= 1;
      else dislikeCount -= 1;
      userVoteType = null;
    } else {
      // switch/toggle on
      if (targetType === 'LIKE') {
        likeCount += 1;
        if (userVoteType === 'DISLIKE') dislikeCount -= 1;
      } else {
        dislikeCount += 1;
        if (userVoteType === 'LIKE') likeCount -= 1;
      }
      userVoteType = targetType;
    }

    likeCount = Math.max(0, likeCount);
    dislikeCount = Math.max(0, dislikeCount);

    om.objects.setObjectProperties(reportId, {
      likeCount,
      dislikeCount,
      userVoteType,
    });

    try {
      await fetch('/api/traffic/vote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportId, voteType: targetType }),
      });
    } catch {
      toast.error('Ошибка сети');
      // при желании — можно тут сделать рефетч актуального состояния
    }
  }, []);

  const actionRef = useRef(handleBalloonAction);
  useEffect(() => {
    actionRef.current = handleBalloonAction;
  }, [handleBalloonAction]);

  useEffect(() => {
    isSelectModeRef.current = isSelectMode;
    onMapClickRef.current = onMapClick;
  }, [isSelectMode, onMapClick]);

  // --- Init Map ---
  useEffect(() => {
    if (!shouldLoad || !isMapsLoaded || !mapContainerRef.current || mapRef.current) return;

    const ymaps = (window as any).ymaps;

    ymaps.ready(() => {
      if (!mapContainerRef.current) return;

      const map = new ymaps.Map(
        mapContainerRef.current,
        {
          center: [56.49, 84.97], // Томск
          zoom: 12,
          controls: ['zoomControl'],
        },
        {
          suppressMapOpenBlock: true,
          yandexMapDisablePoiInteractivity: true,
        },
      );
      map.behaviors.disable(['scrollZoom', 'dblClickZoom', 'multiTouch']);
      const objectManager = new ymaps.ObjectManager({
        clusterize: true,
        gridSize: 64,
        clusterDisableClickZoom: false,
      });

      const BalloonLayout = createBalloonLayout(ymaps, map);

      objectManager.objects.options.set({
        iconLayout: 'default#image',
        iconImageSize: [38, 38],
        iconImageOffset: [-19, -38],
        balloonContentLayout: BalloonLayout,
        balloonPanelMaxMapArea: 0,
        openBalloonOnClick: true,
      });

      map.geoObjects.add(objectManager);

      // события из HTML-шаблона балуна
      map.events.add('customballoonaction', (e: any) => {
        const { reportId, action } = e.get('data');
        actionRef.current(reportId, action);
      });

      // клик по карте (создание нового репорта)
      map.events.add('click', (e: any) => {
        if (isSelectModeRef.current) {
          onMapClickRef.current(e.get('coords'));
        }
      });

      mapRef.current = map;
      objectManagerRef.current = objectManager;
    });

    return () => {
      if (mapRef.current) {
        try {
          mapRef.current.destroy();
        } catch {}
        mapRef.current = null;
        objectManagerRef.current = null;
        reportsIndexRef.current = new Map(); // сброс индекса
      }
    };
  }, [isMapsLoaded, shouldLoad]);

  // --- Cursor ---
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.container.getElement().style.cursor = isSelectMode ? 'crosshair' : 'grab';
    }
  }, [isSelectMode]);

  // --- Batch update reports (init + diff) ---
  useEffect(() => {
    const om = objectManagerRef.current;
    if (!om) return;

    const prevIndex = reportsIndexRef.current;
    const nextIndex = new Map<string, Sig>();

    // ✅ первый прогон — заливаем всё одним батчем
    if (prevIndex.size === 0 && reports.length) {
      om.add({
        type: 'FeatureCollection',
        features: reports.map(toFeature),
      });

      for (const r of reports) nextIndex.set(r.id, makeSig(r));
      reportsIndexRef.current = nextIndex;
      return;
    }

    // ✅ последующие — дифф по сигнатурам
    for (const r of reports) {
      const sig = makeSig(r);
      nextIndex.set(r.id, sig);

      const prevSig = prevIndex.get(r.id);
      if (!prevSig) {
        om.add(toFeature(r));
        continue;
      }

      if (prevSig !== sig) {
        om.objects.setObjectProperties(r.id, {
          comment: r.comment,
          imageUrl: r.imageUrl,
          likeCount: r.likes,
          dislikeCount: r.dislikes,
          commentCount: r.comments.length,
          userVoteType: r.myVote ?? null,
          authorName: r.author.name,
          time: formatTime(r.createdAt),
        });

        om.objects.setObjectOptions(r.id, {
          iconImageHref: EVENT_TYPE_ICONS[r.type] || EVENT_TYPE_ICONS.OTHER,
        });
      }
    }

    // удаляем то, чего больше нет
    for (const id of prevIndex.keys()) {
      if (!nextIndex.has(id)) {
        try {
          om.remove(id);
        } catch {}
      }
    }

    reportsIndexRef.current = nextIndex;
  }, [reports, toFeature]);

  // --- GLOBAL LISTENER (SSE) ---
  useTrafficGlobalListener(
    useCallback(
      (event) => {
        const om = objectManagerRef.current;
        if (!om) return;

        if (event.type === 'VOTE_UPDATE') {
          const { reportId, likes, dislikes } = event.payload;
          const obj = om.objects.getById(reportId);
          if (obj) {
            om.objects.setObjectProperties(reportId, {
              likeCount: likes,
              dislikeCount: dislikes,
            });
          }
          return;
        }

        if (event.type === 'REPORTS_CLEANED') {
          const ids: string[] = event.payload.deletedIds ?? [];
          if (ids.length) {
            try {
              om.remove(ids);
            } catch {}
            ids.forEach((id) => reportsIndexRef.current.delete(id));
          }
          return;
        }

        if (event.type === 'NEW_REPORT') {
          const r: ReportWithDetails = event.payload;
          const idx = reportsIndexRef.current;
          if (idx.has(r.id)) return;

          om.add(toFeature(r));
          idx.set(r.id, makeSig(r));
          return;
        }

        if (event.type === 'DELETE_REPORT') {
          const { reportId } = event.payload;
          try {
            om.remove(reportId);
          } catch {}
          reportsIndexRef.current.delete(reportId);
          return;
        }

        if (event.type === 'NEW_COMMENT') {
          const { reportId } = event.payload;
          const obj = om.objects.getById(reportId);
          if (!obj) return;

          const cur = Number(obj.properties?.commentCount ?? 0);
          om.objects.setObjectProperties(reportId, { commentCount: cur + 1 });
          return;
        }

        if (event.type === 'DELETE_COMMENT') {
          const { reportId } = event.payload;
          const obj = om.objects.getById(reportId);
          if (!obj) return;

          const cur = Number(obj.properties?.commentCount ?? 0);
          om.objects.setObjectProperties(reportId, { commentCount: Math.max(0, cur - 1) });
          return;
        }
      },
      [toFeature],
    ),
  );

  return {
    mapContainerRef,
    isMapsLoaded,
  };
}
